/**
 * The forced ending a workflow task step completed after (agion/Agion#2460).
 *
 * The reasons and what each means are one Go list
 * (platform/shared/contract/missionstream/missionstream_forced_reason.go),
 * generated into generated/missionstream.ts as ForcedReason and ForcedReasons.
 * A completed node status carries one as forced_reason: the step passed with
 * valid outputs, but the runner had to force the model to finish, so the
 * answer may be incomplete. The design system's ExecutionLog turns a reason
 * into its warning; this module is the one reading of the wire value.
 *
 * A runner may send a reason as its uppercase Problem Code (condition C1 of
 * challenge 2026-10-09-p095-execution-policy-contract, Amendment S01-2,
 * agion/Agion#2919). The one lowercase mapping, generated from Go as
 * ForcedReasonByCode, reads a code as the wire name the warnings are keyed on,
 * so both spellings show the same warning.
 */

import { ForcedReasonByCode, ForcedReasons, type ForcedReason } from './generated/missionstream'

const KNOWN: ReadonlySet<string> = new Set(ForcedReasons.map((row) => row.forced_reason))

const BY_CODE: ReadonlyMap<string, ForcedReason> = (() => {
  const byCode = new Map<string, ForcedReason>()
  for (const code of Object.keys(ForcedReasonByCode)) {
    const reason = ForcedReasonByCode[code]
    if (reason) byCode.set(code, reason)
  }
  return byCode
})()

/**
 * The forced reason a wire value names in either spelling, as its wire name,
 * or undefined for an absent, empty or unknown value, so an app drops what
 * this build does not know, as every hop of the stream does.
 */
export function knownForcedReason(value: unknown): ForcedReason | undefined {
  if (typeof value !== 'string') return undefined
  return KNOWN.has(value) ? (value as ForcedReason) : BY_CODE.get(value)
}
